/**
 * Idle state for the video panel when no source is selected — a camera
 * viewfinder on standby. Pure CSS (no MJPEG connection is opened), so an idle
 * session costs zero stream bandwidth and no backend reader.
 */
export default function VideoStandby() {
  return (
    <div style={{
      position: 'absolute', inset: 0, overflow: 'hidden', background: '#07090d',
      fontFamily: 'var(--font-mono)',
    }}>
      <style>{`
        @keyframes vp-standby-blink { 50% { opacity: 0.25; } }
        @keyframes vp-standby-cursor { 50% { opacity: 0; } }
      `}</style>
      {/* scanline shimmer + vignette */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.35,
        background: 'repeating-linear-gradient(0deg, transparent 0 2px, rgba(255,255,255,0.014) 2px 4px)',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)',
      }} />
      {/* viewfinder corner brackets */}
      {([['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']] as const).map(([v, h]) => (
        <div key={`${v}-${h}`} style={{
          position: 'absolute', [v]: '6%', [h]: '5%', width: 26, height: 26,
          borderTop: v === 'top' ? '2px solid rgba(var(--color-accent-rgb),0.75)' : 'none',
          borderBottom: v === 'bottom' ? '2px solid rgba(var(--color-accent-rgb),0.75)' : 'none',
          borderLeft: h === 'left' ? '2px solid rgba(var(--color-accent-rgb),0.75)' : 'none',
          borderRight: h === 'right' ? '2px solid rgba(var(--color-accent-rgb),0.75)' : 'none',
        }} />
      ))}
      {/* crosshair */}
      <div style={{ position: 'absolute', left: '50%', top: '42%', opacity: 0.5 }}>
        <div style={{ position: 'absolute', left: -18, top: 0, width: 36, height: 1, background: 'var(--color-accent)' }} />
        <div style={{ position: 'absolute', top: -18, left: 0, height: 36, width: 1, background: 'var(--color-accent)' }} />
        <div style={{ position: 'absolute', left: -3, top: -3, width: 6, height: 6, border: '1px solid var(--color-accent)', borderRadius: '50%' }} />
      </div>
      {/* top status row */}
      <div style={{ position: 'absolute', top: '7%', left: '13%', display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{
          width: 7, height: 7, borderRadius: '50%', background: '#5a6672',
          animation: 'vp-standby-blink 2s steps(2) infinite',
        }} />
        <span style={{ color: '#8fa0ad', fontSize: 10, letterSpacing: 2 }}>NO FEED</span>
      </div>
      <div style={{ position: 'absolute', top: '7%', right: '13%', color: '#8fa0ad', fontSize: 10, letterSpacing: 2 }}>
        STANDBY MODE
      </div>
      {/* centre text */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: '52%', textAlign: 'center' }}>
        <div style={{ color: '#e8fff2', fontSize: 17, fontWeight: 600, letterSpacing: 7 }}>
          STANDBY<span style={{ animation: 'vp-standby-cursor 1.1s steps(2) infinite' }}>_</span>
        </div>
        <div style={{ marginTop: 8, color: '#44525f', fontSize: 9, letterSpacing: 2 }}>
          SELECT A VIDEO SOURCE TO BEGIN MONITORING
        </div>
      </div>
      {/* bottom meta */}
      <div style={{ position: 'absolute', bottom: '8%', left: '13%', color: '#44525f', fontSize: 9, letterSpacing: 1.5, lineHeight: 1.8 }}>
        SRC <b style={{ color: '#5f7365' }}>——</b><br />LINK <b style={{ color: '#5f7365' }}>IDLE</b>
      </div>
      <div style={{ position: 'absolute', bottom: '8%', right: '13%', display: 'flex', alignItems: 'flex-end', gap: 3, height: 18 }}>
        {[20, 40, 60, 80, 100].map(h => (
          <i key={h} style={{ width: 4, height: `${h}%`, background: '#2a3642', display: 'block' }} />
        ))}
      </div>
    </div>
  );
}
